import { useCallback, useEffect, useRef, useState } from 'react';

export type VideoPlayerRef = {
  togglePlayPause: () => void;
};

interface VideoPlayerProps {
  videoUrl: string;
  videoKey?: string | number;
  onVideoEnded?: () => void;
  onProgressChange?: (progress: number) => void;
  onPlayPauseChange?: (isPlaying: boolean) => void;
  playerRef?: React.MutableRefObject<VideoPlayerRef | null>;
  children?: React.ReactNode;
  className?: string;
  toggleOnClick?: boolean;
  showHoverGradient?: boolean;
  /** When true, the video autoplays with no controls and no hover styles */
  autoplayOnly?: boolean;
}

export default function VideoPlayer({
  videoUrl,
  videoKey,
  onVideoEnded,
  onProgressChange,
  onPlayPauseChange,
  playerRef,
  children,
  className,
  toggleOnClick = true,
  showHoverGradient = true,
  autoplayOnly = false,
}: VideoPlayerProps) {
  const [isPlaying, setIsPlaying] = useState(true);
  const [showStrongGradient, setShowStrongGradient] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const gradientTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleVideoEnded = useCallback(() => {
    onVideoEnded?.();
  }, [onVideoEnded]);

  const handleTimeUpdate = useCallback(() => {
    if (videoRef.current) {
      const { currentTime, duration } = videoRef.current;
      if (duration > 0) {
        onProgressChange?.(currentTime / duration);
      }
    }
  }, [onProgressChange]);

  const togglePlayPause = useCallback(() => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
        setIsPlaying(false);
        onPlayPauseChange?.(false);
      } else {
        videoRef.current.play();
        setIsPlaying(true);
        onPlayPauseChange?.(true);
        // Immediately shrink gradient when playing starts
        setShowStrongGradient(false);
        if (gradientTimeoutRef.current) {
          clearTimeout(gradientTimeoutRef.current);
        }
      }
    }
  }, [isPlaying, onPlayPauseChange]);

  // Expose toggle method via ref prop
  useEffect(() => {
    if (playerRef) {
      playerRef.current = {
        togglePlayPause,
      };
    }
  }, [playerRef, togglePlayPause]);

  // Listen for play/pause events from video element
  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      const handlePlay = () => {
        setIsPlaying(true);
        onPlayPauseChange?.(true);
      };
      const handlePause = () => {
        setIsPlaying(false);
        onPlayPauseChange?.(false);
      };

      video.addEventListener('play', handlePlay);
      video.addEventListener('pause', handlePause);

      return () => {
        video.removeEventListener('play', handlePlay);
        video.removeEventListener('pause', handlePause);
      };
    }
  }, [videoKey, onPlayPauseChange]);

  // Clear timeout on unmount
  useEffect(() => {
    return () => {
      if (gradientTimeoutRef.current) {
        clearTimeout(gradientTimeoutRef.current);
      }
    };
  }, []);

  const handleMouseMove = useCallback(() => {
    // Only handle gradient timeout when playing
    if (!isPlaying) return;

    setShowStrongGradient(true);

    // Clear existing timeout
    if (gradientTimeoutRef.current) {
      clearTimeout(gradientTimeoutRef.current);
    }

    // Set new timeout to shrink gradient after 1 second of no movement
    gradientTimeoutRef.current = setTimeout(() => {
      setShowStrongGradient(false);
    }, 1000);
  }, [isPlaying]);

  const handleMouseLeave = useCallback(() => {
    // Clear timeout and shrink gradient when leaving
    if (gradientTimeoutRef.current) {
      clearTimeout(gradientTimeoutRef.current);
    }
    if (isPlaying) {
      setShowStrongGradient(false);
    }
  }, [isPlaying]);

  // Gradient is strong when: paused OR (playing and showStrongGradient)
  const allowClickToToggle = !autoplayOnly && toggleOnClick;
  const allowHoverGradient = !autoplayOnly && showHoverGradient;
  const isGradientStrong =
    !isPlaying || (allowHoverGradient && showStrongGradient);

  return (
    <div
      className={`relative overflow-hidden ${allowHoverGradient || allowClickToToggle ? 'group' : ''} ${allowClickToToggle ? 'cursor-pointer' : ''} ${className}`}
      onClick={allowClickToToggle ? togglePlayPause : undefined}
      onMouseMove={allowHoverGradient ? handleMouseMove : undefined}
      onMouseLeave={allowHoverGradient ? handleMouseLeave : undefined}
    >
      {/* Video */}
      <video
        ref={videoRef}
        key={videoKey}
        src={videoUrl}
        autoPlay
        muted
        loop={autoplayOnly}
        playsInline
        disablePictureInPicture
        onEnded={autoplayOnly ? undefined : handleVideoEnded}
        onTimeUpdate={autoplayOnly ? undefined : handleTimeUpdate}
        className="absolute inset-0 h-full w-full object-cover"
      />

      {/* Strong gradient (fades in/out based on pause/hover state) */}
      {!autoplayOnly && (
        <div
          className={`pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0.00)_18.46%,#000_106.12%)] transition-opacity duration-500 ${
            isGradientStrong ? 'opacity-30' : 'opacity-0'
          }`}
        />
      )}

      {/* Children overlay (e.g., controls, tabs) */}
      {children}
    </div>
  );
}
